import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, getArticlesByCategory } from "@/mocks/articles";
import { categories, getCategory } from "@/mocks/categories";
import { formatHeat } from "@/utils/format";

type SortKey = "latest" | "hot";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategory(slug);
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const list = useMemo(() => {
    const base = category ? getArticlesByCategory(category.slug) : [];
    return [...base].sort((a, b) =>
      sort === "hot" ? b.heat - a.heat : a.publishedAt < b.publishedAt ? 1 : -1,
    );
  }, [category, sort]);

  const hotList = useMemo(() => [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5), []);

  if (!category) {
    return (
      <SiteLayout>
        <Seo
          title="栏目不存在 | 网红大瓜"
          description="你访问的瓜类栏目不存在，返回首页继续吃瓜。"
          canonicalPath="/category"
        />
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:py-32">
          <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-emotion-unhappy-line text-3xl" />
          </span>
          <h1 className="mb-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            这个栏目暂时不存在
          </h1>
          <p className="mb-8 text-sm text-foreground-600">
            可能是链接输错了，也可能是栏目已经调整。回到首页看看今天的热门大瓜吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const crumbs = [
    { label: "首页", path: "/" },
    { label: category.name, path: `/category/${category.slug}` },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${category.name} | 网红大瓜${category.name}栏目 - 原帖与截图集中看`}
        description={category.description}
        keywords={[category.name, `网红大瓜${category.name}`, "网红大瓜猛料曝光", "热点事件时间线"]}
        canonicalPath={`/category/${category.slug}`}
        image={category.image}
        imageAlt={category.imageAlt}
        breadcrumbs={crumbs}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 网红大瓜`,
            description: category.description,
            url: `https://chengshunjt.cn/category/${category.slug}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${category.name}瓜帖列表`,
            itemListElement: list.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              url: `https://chengshunjt.cn/article/${item.id}`,
            })),
          },
        ]}
      />

      <section className="relative overflow-hidden border-b border-background-200 bg-foreground-950">
        <img
          src={category.image}
          alt={category.imageAlt}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/45" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
          <div className="mb-4 rounded-md bg-background-50/10 px-3 py-2 backdrop-blur-sm w-fit">
            <div className="[&_a]:text-background-200 [&_span]:text-background-50 [&_ol]:text-background-300">
              <Breadcrumb items={crumbs} />
            </div>
          </div>
          <span className="mb-3 inline-flex items-center gap-2 text-xs font-medium tracking-[0.25em] text-accent-400">
            <i className="ri-price-tag-3-line" />
            CATEGORY
          </span>
          <h1 className="mb-4 font-heading text-3xl font-black text-background-50 md:text-5xl">
            {category.name}
          </h1>
          <p className="max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            {category.intro}
          </p>
          <p className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-background-300">
            <span className="flex items-center gap-1.5">
              <i className="ri-file-list-3-line" />
              共 {list.length} 条瓜帖
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line" />
              按热度与时间持续补充
            </span>
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground-600">
                当前按 <strong className="font-medium text-foreground-900">{sort === "hot" ? "热度" : "最新"}</strong> 排序，
                共 {list.length} 条
              </p>
              <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                {([
                  { key: "latest", label: "最新发布" },
                  { key: "hot", label: "热度最高" },
                ] as { key: SortKey; label: string }[]).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setSort(tab.key);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                      sort === tab.key
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {list.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
                <p className="text-sm text-foreground-500">这个栏目还没有内容，编辑部正在整理中。</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {list.slice(0, visible).map((article) => (
                  <ArticleCard key={article.id} article={article} variant="default" />
                ))}
              </div>
            )}

            {visible < list.length && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-add-line" />
                  加载更多（还有 {list.length - visible} 条）
                </button>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  全站热度榜
                </h2>
                <ol className="divide-y divide-background-200">
                  {hotList.map((article, index) => (
                    <li key={article.id}>
                      <ArticleCard article={article} variant="compact" rank={index + 1} />
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-apps-2-line text-primary-500" />
                  切换栏目
                </h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/category/${item.slug}`}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                        item.slug === category.slug
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-accent-300 bg-accent-100 p-5">
                <h2 className="mb-2 flex items-center gap-2 font-heading text-base font-bold text-accent-950">
                  <i className="ri-lightbulb-line" />
                  看榜小提示
                </h2>
                <p className="text-sm leading-relaxed text-accent-900">
                  热度榜只统计站内讨论量，排名高不代表结论成立。建议点进详情页看完整时间线，再决定怎么判断。
                </p>
                <p className="mt-3 text-xs text-accent-800">
                  当前最热：{hotList[0] ? `${hotList[0].title.slice(0, 18)}…（${formatHeat(hotList[0].heat)}）` : "暂无"}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}